import { useQuery, type QueryClient } from '@tanstack/react-query';
import { api, podatki, type Shema } from '../api/odjemalec';
import { datum, denar, koliko, odstotek, stevilo, tocke } from '../oblika/oblika';

export type Pogovor = Shema['AsistentPogovor'];
export type Sporocilo = Shema['AsistentSporocilo'];
export type Odgovor = Shema['AsistentOdgovor'];

export const KLJUC_ASISTENT = ['asistent'] as const;

/** Kaj asistent zna (s strežnika, brez klica modela) – za prazen pogovor. */
export function useZmoznosti() {
  return useQuery({
    queryKey: [...KLJUC_ASISTENT, 'zmoznosti'],
    queryFn: ({ signal }) => podatki(api.GET('/api/asistent/zmoznosti', { signal })),
    staleTime: 3_600_000,
  });
}

/** Pogovori uporabnika, zadnji najprej. */
export function usePogovori() {
  return useQuery({
    queryKey: [...KLJUC_ASISTENT, 'pogovori'],
    queryFn: ({ signal }) =>
      podatki(api.GET('/api/asistent/pogovori', { signal, params: { query: { stran: 0, velikost: 100 } } })),
  });
}

/** Pogovor s sporočili; kartice strežnik ob vsakem branju izračuna iz trenutnih podatkov. */
export function usePogovor(id: number | null) {
  return useQuery({
    queryKey: [...KLJUC_ASISTENT, 'pogovor', id],
    enabled: id !== null,
    queryFn: ({ signal }) =>
      podatki(api.GET('/api/asistent/pogovori/{id}', { signal, params: { path: { id: id ?? 0 } } })),
  });
}

/**
 * Odgovor na vprašanje doda v pogovor v predpomnilniku (nov pogovor ustvari), osveži seznam pogovorov in stanje točk.
 */
export function shraniOdgovor(odjemalec: QueryClient, o: Odgovor) {
  const id = o.pogovorId ?? 0;
  odjemalec.setQueryData<Pogovor>([...KLJUC_ASISTENT, 'pogovor', id], (prej) => ({
    ...(prej ?? { id, naslov: o.naslov, ustvarjeno: Date.now(), zadnjeSporocilo: Date.now() }),
    sporocila: [...(prej?.sporocila ?? []), ...(o.sporocila ?? [])],
  }));
  void odjemalec.invalidateQueries({ queryKey: [...KLJUC_ASISTENT, 'pogovori'] });
  void odjemalec.invalidateQueries({ queryKey: ['ai-stanje'] });
}

/** Sporočilo s spremenjeno kartico (parametri, komentar) zamenja tisto v pogovoru v predpomnilniku. */
export function zamenjajSporocilo(odjemalec: QueryClient, pogovorId: number, novo: Sporocilo) {
  odjemalec.setQueryData<Pogovor>([...KLJUC_ASISTENT, 'pogovor', pogovorId], (prej) =>
    prej ? { ...prej, sporocila: (prej.sporocila ?? []).map((s) => (s.id === novo.id ? novo : s)) } : prej,
  );
}

/** »0,000854 točke«, »1 točka«, »2 točki« – natančno kot na strežniku (brez zaokroževanja na cent). */
export function porabljeno(t: number): string {
  if (Number.isInteger(t)) {
    return koliko(t, ['točka', 'točki', 'točke', 'točk']);
  }
  return `${tocke(t)} točke`;
}

/** Oznaka stanja zahteve, ki je asistent še ne zna. */
export function stanjeZahteve(stanje: string | undefined): { naziv: string; barva: string } {
  switch (stanje) {
    case 'V_DELU':
      return { naziv: 'v delu', barva: 'blue' };
    case 'UREJENO':
      return { naziv: 'urejeno', barva: 'green' };
    case 'ZAVRNJENO':
      return { naziv: 'ne bo dodano', barva: 'gray' };
    default:
      return { naziv: 'posredovano', barva: 'yellow' };
  }
}

/** Vrednost s kartice asistenta za prikaz po vrsti stolpca (DENAR, STEVILO, ODSTOTEK, DNI, DATUM, BESEDILO). */
export function vrednost(v: unknown, vrsta: string | undefined): string {
  if (v === undefined || v === null || v === '') {
    return '–';
  }
  if (typeof v !== 'number') {
    return vrsta === 'DATUM' ? datum(String(v)) : String(v);
  }
  switch (vrsta) {
    case 'DENAR':
      return `${denar(v)} €`;
    case 'ODSTOTEK':
      return `${odstotek(v)} %`;
    case 'DNI':
      return koliko(Math.round(v), ['dan', 'dneva', 'dnevi', 'dni']);
    case 'STEVILO':
      return Number.isInteger(v) ? stevilo(v) : odstotek(v);
    default:
      return String(v);
  }
}

/** Sprememba glede na primerjalno obdobje: »+12,5 %«, »-3 %«. */
export function sprememba(s: number): string {
  return `${s > 0 ? '+' : ''}${odstotek(Math.round(s * 10) / 10)} %`;
}
